/**
 * Filtro de "asignado a" del selector global de ámbito.
 *
 * `miembroFiltro` puede contener uno o varios valores separados por coma:
 *   "uuid"                → solo ese miembro
 *   "uuid1,uuid2"         → cualquiera de los dos
 *   "sin_asignar"         → sin responsable
 *   "uuid1,sin_asignar"   → ese miembro o sin responsable
 */
export const SIN_ASIGNAR = "sin_asignar" as const;

export type MiembroFiltroParsed = { ids: string[]; sinAsignar: boolean };

export function parseMiembroFiltro(v: string | null | undefined): MiembroFiltroParsed {
  if (!v) return { ids: [], sinAsignar: false };
  const parts = Array.from(
    new Set(
      String(v)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  );
  return {
    ids: parts.filter((p) => p !== SIN_ASIGNAR),
    sinAsignar: parts.includes(SIN_ASIGNAR),
  };
}

export function serializeMiembroFiltro(p: MiembroFiltroParsed): string | null {
  const parts = [...p.ids];
  if (p.sinAsignar) parts.push(SIN_ASIGNAR);
  return parts.length ? parts.join(",") : null;
}

export function toggleMiembroFiltro(
  current: string | null | undefined,
  value: string,
): string | null {
  const p = parseMiembroFiltro(current);
  if (value === SIN_ASIGNAR) p.sinAsignar = !p.sinAsignar;
  else if (p.ids.includes(value)) p.ids = p.ids.filter((x) => x !== value);
  else p.ids.push(value);
  return serializeMiembroFiltro(p);
}

export function miembroFiltroIncluye(
  current: string | null | undefined,
  value: string,
): boolean {
  const p = parseMiembroFiltro(current);
  return value === SIN_ASIGNAR ? p.sinAsignar : p.ids.includes(value);
}

type FilterableQuery = {
  eq: (col: string, v: any) => any;
  in: (col: string, v: any[]) => any;
  is: (col: string, v: null) => any;
  or: (expr: string) => any;
};

/**
 * Aplica el filtro de asignado a una query PostgREST. Devuelve la query
 * (posiblemente sin tocar si no hay filtro).
 */
export function applyAsignadoFilter<Q extends FilterableQuery>(
  q: Q,
  filtro: string | null | undefined,
  columna = "asignado_a",
): Q {
  const { ids, sinAsignar } = parseMiembroFiltro(filtro);
  if (!ids.length && !sinAsignar) return q;
  if (!ids.length) return q.is(columna, null) as Q;
  if (!sinAsignar) {
    return (ids.length === 1 ? q.eq(columna, ids[0]) : q.in(columna, ids)) as Q;
  }
  return q.or(`${columna}.in.(${ids.join(",")}),${columna}.is.null`) as Q;
}
